import {
    BaseScoreResult,
    QuestionnaireResponse,
    Item,
    findItemByLinkId,
    getAnswerCode
} from './types';

export interface MocabScoreResult extends BaseScoreResult {
    totalScore: number;
    adjustedScore: number;
    adjustments: {
        schoolingAdjustment: number;
        literacyAdjustment: number;
    };
    details: Record<string, number>;
}

const SCORE_EXTENSION_URL = "http://hl7.org/fhir/StructureDefinition/questionnaire-optionScore";

function getItemScoreFromOption(item: Item | undefined): number {
    if (!item) return 0;
    const code = getAnswerCode(item);
    if (code === undefined) return 0;

    const selectedOption = item.answerOption?.find(opt => opt.valueCoding?.code === code);
    const scoreExtension = selectedOption?.extension?.find(ext => ext.url === SCORE_EXTENSION_URL);
    const score = scoreExtension?.valueInteger ?? parseInt(code, 10);
    return Number.isFinite(score) ? score : 0;
}

export function processMocabQuestionnaire(response: QuestionnaireResponse): MocabScoreResult {
    const rootItems = response.item || [];

    const scoringLinkIds: string[] = [
        'execucao',
        'fluencia',
        'orientacao',
        'calculo',
        'abstracao',
        'evocacao_tardia',
        'percepcao',
        'nomeacao',
        'atencao_simples',
        'atencao_complexa'
    ];

    const details: Record<string, number> = {};
    let totalScore = 0;

    scoringLinkIds.forEach(linkId => {
        const item = findItemByLinkId(rootItems, linkId);
        const score = getItemScoreFromOption(item);
        details[linkId] = score;
        totalScore += score;
    });

    const escolaridadeYears = (() => {
        const item = findItemByLinkId(rootItems, 'escolaridade');
        const raw = item?.answer?.[0]?.valueInteger ?? (item?.answer?.[0]?.valueString ? Number(item?.answer?.[0]?.valueString) : undefined);
        return Number.isFinite(raw as any) ? (raw as number) : undefined;
    })();

    const isIlliterate = (() => {
        const literacyItem = findItemByLinkId(rootItems, 'alfabetizado') || findItemByLinkId(rootItems, 'alfabetizacao');
        const code = getAnswerCode(literacyItem);
        if (code === '0') return true;
        return false;
    })();

    let schoolingAdjustment = 0;
    let literacyAdjustment = 0;

    if (totalScore < 30) {
        if (typeof escolaridadeYears === 'number' && escolaridadeYears < 4) {
            schoolingAdjustment = 1;
        }
        if (isIlliterate) {
            literacyAdjustment = 1;
        }
    }

    const adjustedScore = Math.min(30, totalScore + schoolingAdjustment + literacyAdjustment);

    return {
        questionnaireId: response.questionnaire?.split('/')?.[1],
        questionnaireResponseId: response.id,
        totalScore,
        adjustedScore,
        adjustments: {
            schoolingAdjustment,
            literacyAdjustment
        },
        details
    };
}


